import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRightIcon, ExternalLinkIcon, FileTextIcon, NetworkIcon, RotateCcwIcon, ShieldAlertIcon } from 'lucide-react';
import { CaprelsaWordmark } from './CaprelsaWordmark';
import { InfoSheet } from './InfoSheet';
import { EMA_EPAR_URL, ZETA_REFERENCE, evidenceSummary, keySafety, zetaPfs } from '../../data/caprelsa';
type ContinueSceneProps = {
  onMechanism: () => void;
  onReplay: () => void;
  still?: boolean;
};
type Panel = 'evidence' | 'safety' | null;
export function ContinueScene({
  onMechanism,
  onReplay,
  still = false
}: ContinueSceneProps) {
  const [panel, setPanel] = useState<Panel>(null);
  const options = [{
    label: 'Explore the evidence',
    icon: FileTextIcon,
    onClick: () => setPanel('evidence')
  }, {
    label: 'Understand the mechanism',
    icon: NetworkIcon,
    onClick: onMechanism
  }, {
    label: 'Review safety information',
    icon: ShieldAlertIcon,
    onClick: () => setPanel('safety')
  }];
  return <div className="flex h-full flex-col" data-id="element-456" data-mp="3b4qai5jul0wr">
      <div className="relative mt-4 flex h-[190px] items-center justify-center" data-id="element-457" data-mp="3b5rl68wybldy">
        {[160, 210, 260].map((size, i) => <motion.span key={size} className="absolute rounded-full border border-cap-gold/40" style={{
        width: size,
        height: size
      }} initial={{
        opacity: still ? 0.6 : 0,
        scale: still ? 1 : 0.8
      }} animate={still ? undefined : {
        opacity: [0.8, 0.2, 0.8],
        scale: [1, 1.04, 1]
      }} transition={{
        duration: 3.2,
        repeat: Infinity,
        delay: i * 0.4,
        ease: 'easeInOut'
      }} aria-hidden="true" data-id="element-458" data-mp="367zx5pzfgjf1" data-mpi={i} />)}
        <CaprelsaWordmark size="lg" data-id="element-459" data-mp="3l1bfwrzs4e14" />
      </div>

      <h2 className="mt-4 text-center text-[29px] font-extrabold leading-[1.05] tracking-[-0.03em] text-cap-purple" data-id="element-460" data-mp="3g4l1e93mhqht">Discover the clinical evidence.</h2>
      <p className="mt-2 text-center text-[13.5px] text-cap-grey" data-id="element-461" data-mp="hfdv18y8odc2">Where would you like to go next?</p>

      <ul className="mt-6 space-y-2.5" data-id="element-462" data-mp="cm1ezn7mleux">
        {options.map(o => <li key={o.label} data-id="element-463" data-mp="vkwaik9js8id">
            <button type="button" onClick={o.onClick} className="flex h-14 w-full items-center gap-3 rounded-2xl bg-white px-4 text-left text-[14px] font-bold text-cap-purple ring-1 ring-cap-line transition-[background-color,box-shadow,transform] duration-150 hover:bg-cap-lilac hover:ring-cap-purple/30 active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-cap-gold" data-id="element-464" data-mp="vlo63v35a68n">
              <o.icon size={18} className="text-cap-gold" aria-hidden="true" data-id="element-465" data-mp="ht7xmerh5g0c" />
              <span className="flex-1" data-id="element-466" data-mp="3sx6dam95zen">{o.label}</span>
              <ChevronRightIcon size={16} className="text-cap-purple/40" aria-hidden="true" data-id="element-467" data-mp="3u8cvi731l9g" />
            </button>
          </li>)}
      </ul>

      <div className="mt-auto flex items-center justify-between pt-4" data-id="element-468" data-mp="1y5wpvetr7dr7">
        <a href={EMA_EPAR_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[11px] font-bold text-cap-purple underline-offset-2 hover:underline" data-id="element-469" data-mp="2281j4cgai8db">
          EU prescribing information <ExternalLinkIcon size={11} aria-hidden="true" data-id="element-470" data-mp="3iqbpspy8xxal" />
        </a>
        <button type="button" onClick={onReplay} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-cap-grey hover:text-cap-purple" data-id="element-471" data-mp="bj81dlhbsgk">
          <RotateCcwIcon size={12} aria-hidden="true" data-id="element-472" data-mp="a8nmer932jmt" /> Replay
        </button>
      </div>

      <InfoSheet open={panel === 'evidence'} title="The ZETA trial" onClose={() => setPanel(null)} data-id="element-473" data-mp="aai9oc7tunu2">
        <div className="grid grid-cols-3 gap-2" data-id="element-474" data-mp="5bx4dydpxdyr">
          {evidenceSummary.map(e => <div key={e.label} className="rounded-xl bg-cap-lilac p-2.5" data-id="element-475" data-mp="j7998yll8kte">
              <p className="text-[15px] font-extrabold text-cap-purple" data-id="element-476" data-mp="easbrdsi0u3n">{e.value}</p>
              <p className="mt-0.5 text-[9.5px] leading-tight text-cap-grey" data-id="element-477" data-mp="2l1i2ck9lq30p">{e.label}</p>
            </div>)}
        </div>
        <dl className="mt-4 space-y-2.5 text-[12px] leading-snug" data-id="element-478" data-mp="2g3qcrwwfiszo">
          <div data-id="element-479" data-mp="2ifip0pizpe8g">
            <dt className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-cap-gold" data-id="element-480" data-mp="2ig0v0dvgf6tk">Design</dt>
            <dd className="text-cap-ink" data-id="element-481" data-mp="2mg2ow0uy6vya">{zetaPfs.design}</dd>
          </div>
          <div data-id="element-482" data-mp="2ria3ky75wq4b">
            <dt className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-cap-gold" data-id="element-483" data-mp="2rhrz5eacj5lb">Population</dt>
            <dd className="text-cap-ink" data-id="element-484" data-mp="2wdz7teu4mv6y">{zetaPfs.population}</dd>
          </div>
          <div data-id="element-485" data-mp="1ytw5kig2ptsr">
            <dt className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-cap-gold" data-id="element-486" data-mp="1yq8b9h2t13cz">Endpoint</dt>
            <dd className="text-cap-ink" data-id="element-487" data-mp="23pbecvz84zib">
              {zetaPfs.endpoint}. Median {zetaPfs.caprelsa} months (predicted) vs {zetaPfs.placebo} months with placebo.
            </dd>
          </div>
        </dl>
        <ul className="mt-3 space-y-1.5 text-[10.5px] leading-snug text-cap-grey" data-id="element-488" data-mp="2dj0ra9m2ug5m">
          {zetaPfs.notes.map(n => <li key={n} data-id="element-489" data-mp="1l0d84zqn5b76">· {n}</li>)}
        </ul>
        <p className="mt-3 font-mono text-[9.5px] text-cap-grey" data-id="element-490" data-mp="1kytrh812oy4v">{ZETA_REFERENCE}</p>
      </InfoSheet>

      <InfoSheet open={panel === 'safety'} title="Important safety information" onClose={() => setPanel(null)} data-id="element-491" data-mp="1dfb12gpi22m9">
        <ul className="space-y-2.5" data-id="element-492" data-mp="18gg6khi66pmb">
          {keySafety.map((s, i) => <li key={s} className={`rounded-xl p-3 text-[12px] leading-snug ${i === 0 ? 'border-l-4 border-cap-orange bg-[#fdf3ea] font-semibold text-cap-ink' : 'bg-cap-lilac text-cap-ink'}`} data-id="element-493" data-mp="13hd5f85b93jm" data-mpi={i}>
              {s}
            </li>)}
        </ul>
        <a href={EMA_EPAR_URL} target="_blank" rel="noopener noreferrer" className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-cap-purple text-[12px] font-bold tracking-[0.1em] text-white hover:bg-cap-ink" data-id="element-494" data-mp="pqi2sdlenioh">
          FULL EU SmPC (EMA) <ExternalLinkIcon size={13} aria-hidden="true" data-id="element-495" data-mp="atqybpwvjgs0" />
        </a>
        <p className="mt-2 text-center text-[9.5px] text-cap-grey" data-id="element-496" data-mp="6u03nb488gi7">Not a complete list. Refer to the current Summary of Product Characteristics.</p>
      </InfoSheet>
    </div>;
}